/**
 * The settings page layout v2 (`release-account-settings-v2`): which
 * sub-pages exist (`?section=`), how protected the account is, the one next
 * step, and the live one-line summaries on the overview tiles. Pure functions
 * over what the page already loads (`GET /api/account-security/status`, the
 * user's preferences); no new API. The server enforces every rule.
 */

import type { MethodStrength, MfaMethod, MfaMethodType, SecurityStatus } from './domain';

// ── Sub-pages ───────────────────────────────────────────────────────────────

export type SettingsSectionKey = 'overview' | 'profile' | 'security' | 'notifications' | 'communication' | 'schedule' | 'apps' | 'whats-new-categories';

export interface SettingsSection {
  key: SettingsSectionKey;
  /** The `?section=` value; '' for the overview. */
  param: string;
  label: string;
  /** One line under the name in the nav and on its tile when nothing live is known. */
  blurb: string;
  icon: string;
}

const SECTIONS: readonly SettingsSection[] = [
  { key: 'overview', param: '', label: 'Overview', blurb: 'How protected your account is', icon: 'overview' },
  { key: 'profile', param: 'profile', label: 'Profile', blurb: 'Name, email, cell, signature', icon: 'person' },
  { key: 'security', param: 'security', label: 'Sign-in & security', blurb: 'Password, two-step sign-in, backup plan', icon: 'shield' },
  { key: 'notifications', param: 'notifications', label: 'Notifications', blurb: 'Which alerts you get, by push and email', icon: 'bell' },
  { key: 'communication', param: 'communication', label: 'Calls & texts', blurb: 'Calling number, texting number, reply forwarding', icon: 'phone-forward' },
  { key: 'schedule', param: 'schedule', label: 'Schedule', blurb: 'Working hours and time off', icon: 'calendar' },
  { key: 'apps', param: 'apps', label: 'Connected apps', blurb: 'Devices, apps and integrations', icon: 'devices' },
  { key: 'whats-new-categories', param: 'whats-new-categories', label: 'What’s New', blurb: 'Product updates you follow', icon: 'sparkle' },
];

/** What decides whether a sub-page has anything for this person. */
export interface SettingsSectionContext {
  securityCapabilities: boolean;
  dtsDealer: boolean;
  scheduleCapability: boolean;
  whatsNew: boolean;
  hasNotifications: boolean;
  /** Devices, OAuth connections, Google, CARFAX, API keys or the Chrome extension: anything the apps page lists. */
  hasApps: boolean;
}

/** The sub-pages to list, in nav order. Overview and Profile always show. */
export function visibleSettingsSections(ctx: SettingsSectionContext): SettingsSection[] {
  return SECTIONS.filter((section) => {
    switch (section.key) {
      case 'security':
        return ctx.securityCapabilities;
      case 'notifications':
        return ctx.hasNotifications;
      case 'communication':
        return !ctx.dtsDealer;
      case 'schedule':
        return ctx.scheduleCapability;
      case 'apps':
        return ctx.hasApps;
      case 'whats-new-categories':
        return ctx.whatsNew;
      default:
        return true;
    }
  }).map((section) => ({ ...section }));
}

/**
 * The sub-page a `?section=` value opens. Unknown or hidden values fall back to
 * the overview; an account that still owes enrollment always lands on security.
 */
export function resolveSettingsSection(raw: unknown, visible: readonly SettingsSection[], opts: { enrollmentRequired?: boolean } = {}): SettingsSectionKey {
  if (opts.enrollmentRequired) return 'security';
  const param = typeof raw === 'string' ? raw.trim().toLowerCase() : '';
  const match = visible.find((section) => section.param === param && param !== '');
  return match ? match.key : 'overview';
}

export function settingsSectionParam(key: SettingsSectionKey): string {
  return SECTIONS.find((section) => section.key === key)?.param ?? '';
}

export function settingsSection(key: SettingsSectionKey): SettingsSection {
  return { ...(SECTIONS.find((section) => section.key === key) ?? SECTIONS[0]!) };
}

// ── Account protection ─────────────────────────────────────────────────────

export type ProtectionState = 'done' | 'recommended' | 'attention';

export interface ProtectionStep {
  key: 'two-step' | 'strong-method' | 'lost-phone';
  label: string;
  detail: string;
  state: ProtectionState;
}

export interface ProtectionNextAction {
  key: 'add-method' | 'add-passkey' | 'backup-codes' | 'verify-id';
  title: string;
  body: string;
  action: string;
  /** Where the action lives: the security sub-page and the anchor of its row. */
  section: SettingsSectionKey;
  anchor: string;
}

export interface AccountProtection {
  steps: ProtectionStep[];
  done: number;
  total: number;
  /** "2 of 3 recommended steps done" */
  summary: string;
  /** A short verdict for the meter. */
  headline: string;
  tone: 'strong' | 'good' | 'weak';
  next: ProtectionNextAction | null;
}

export interface ProtectionFeatures {
  /** `account-security`: the status carries `backup_codes`. */
  backupCodes: boolean;
  /** `account-security`: a verified ID counts as a backup plan. */
  identity: boolean;
}

const STRONG_TYPES: readonly MfaMethodType[] = ['totp', 'webauthn'];

function isStrong(method: MfaMethod, strength: Partial<Record<MfaMethodType, MethodStrength>> | undefined): boolean {
  const rated = strength ? strength[method.type] : undefined;
  return rated ? rated === 'strong' : STRONG_TYPES.indexOf(method.type) !== -1;
}

/** Backup codes left before the page asks for a new set. */
export const BACKUP_CODES_LOW = 3;

/**
 * Three steps, each read straight off the status: two-step sign-in is on, at
 * least one method can't be phished or SIM-swapped (passkey or app), and a lost
 * phone won't lock you out (enough backup codes, or a verified ID). The first
 * one not done is the single next action.
 */
export function accountProtection(
  status: Pick<SecurityStatus, 'methods' | 'method_strength' | 'backup_codes' | 'identity'> | null | undefined,
  features: ProtectionFeatures,
): AccountProtection {
  const methods = (status && status.methods) || [];
  const hasMethod = methods.length > 0;
  const hasStrong = methods.some((method) => isStrong(method, status?.method_strength));
  const remaining = features.backupCodes && status && status.backup_codes ? Math.max(0, Number(status.backup_codes.remaining) || 0) : null;
  const verified = features.identity && !!status?.identity?.verified;

  const twoStep: ProtectionStep = hasMethod
    ? { key: 'two-step', label: 'Two-step sign-in', detail: methods.length === 1 ? '1 sign-in method' : `${methods.length} sign-in methods`, state: 'done' }
    : { key: 'two-step', label: 'Two-step sign-in', detail: 'Not set up yet', state: 'attention' };

  const strong: ProtectionStep = hasStrong
    ? { key: 'strong-method', label: 'A passkey or authenticator app', detail: 'Can’t be stolen with your phone number', state: 'done' }
    : { key: 'strong-method', label: 'A passkey or authenticator app', detail: hasMethod ? 'You only use text or call codes' : 'Safer than text codes', state: 'recommended' };

  let lostState: ProtectionState;
  let lostDetail: string;
  if (remaining !== null && remaining > BACKUP_CODES_LOW) {
    lostState = 'done';
    lostDetail = `${remaining} backup codes saved`;
  } else if (verified) {
    lostState = 'done';
    lostDetail = 'Your photo ID is verified';
  } else if (remaining === null) {
    lostState = 'recommended';
    lostDetail = features.identity ? 'Verify your ID so you can always get back in' : 'Your dealership admin can help you back in';
  } else if (remaining === 0) {
    lostState = 'attention';
    lostDetail = 'No backup codes left';
  } else {
    lostState = 'recommended';
    lostDetail = remaining === 1 ? 'Only 1 backup code left' : `Only ${remaining} backup codes left`;
  }
  const lost: ProtectionStep = { key: 'lost-phone', label: 'A plan for a lost phone', detail: lostDetail, state: lostState };

  const steps = [twoStep, strong, lost];
  const done = steps.filter((step) => step.state === 'done').length;
  const tone = done === steps.length ? 'strong' : done === 0 || steps.some((step) => step.state === 'attention') ? 'weak' : 'good';
  const headline = tone === 'strong' ? 'Your account is well protected' : tone === 'good' ? 'Almost there' : 'Your account needs attention';

  let next: ProtectionNextAction | null = null;
  if (!hasMethod) {
    next = { key: 'add-method', title: 'Turn on two-step sign-in', body: 'A second step means a stolen password alone can’t get anyone into your account.', action: 'Set up two-step sign-in', section: 'security', anchor: 'two-step' };
  } else if (!hasStrong) {
    next = { key: 'add-passkey', title: 'Add a passkey or authenticator app', body: 'Text codes can be redirected if someone takes over your number. A passkey can’t.', action: 'Add a passkey', section: 'security', anchor: 'two-step' };
  } else if (lost.state !== 'done') {
    next =
      remaining !== null
        ? { key: 'backup-codes', title: 'Make new backup codes', body: 'If your phone is lost or stolen, a backup code gets you in on the spot. No need to call anyone.', action: 'Make backup codes', section: 'security', anchor: 'backup-codes' }
        : features.identity
          ? { key: 'verify-id', title: 'Verify your photo ID', body: 'If you ever lose your phone, your ID gets you back in without waiting on anyone.', action: 'Verify my ID', section: 'security', anchor: 'photo-id' }
          : null;
  }

  return { steps, done, total: steps.length, summary: `${done} of ${steps.length} recommended steps done`, headline, tone, next };
}

/** The security tile's live line: "Two-step on · 2 backup codes". */
export function securitySummary(status: Pick<SecurityStatus, 'methods' | 'backup_codes'> | null | undefined, features: { backupCodes: boolean }): string {
  const methods = (status && status.methods) || [];
  const parts = [methods.length ? 'Two-step on' : 'Two-step off'];
  if (features.backupCodes && status && status.backup_codes) {
    const n = Math.max(0, Number(status.backup_codes.remaining) || 0);
    parts.push(n === 1 ? '1 backup code' : `${n} backup codes`);
  }
  return parts.join(' · ');
}

// ── Notifications ───────────────────────────────────────────────────────────

export interface NotificationGroup<T> {
  key: string;
  label: string;
  types: T[];
}

const GROUPS: ReadonlyArray<{ key: string; label: string; match: RegExp }> = [
  { key: 'leads', label: 'Leads', match: /lead|appointment|showroom/i },
  { key: 'messages', label: 'Messages and calls', match: /message|text|sms|chat|conversation|call|voicemail|email_reply|reply|mention/i },
  { key: 'deals', label: 'Deals, credit and titles', match: /deal|credit|finance|title|esign|sign|payment|loan|repair|service|ro_/i },
  { key: 'summaries', label: 'Summaries', match: /daily|summary|digest|report|weekly/i },
];

/** Notification types grouped for scanning, in a fixed group order; anything unmatched goes last under "Other". */
export function groupNotificationTypes<T extends { key: string; name?: string }>(types: readonly T[]): NotificationGroup<T>[] {
  const buckets = new Map<string, T[]>();
  for (const type of types) {
    const hay = `${type.key} ${type.name ?? ''}`;
    const group = GROUPS.find((g) => g.match.test(hay));
    const key = group ? group.key : 'other';
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key)!.push(type);
  }
  const ordered = [...GROUPS.map((g) => ({ key: g.key, label: g.label })), { key: 'other', label: 'Other' }];
  return ordered.filter((g) => buckets.has(g.key)).map((g) => ({ key: g.key, label: g.label, types: buckets.get(g.key)! }));
}

/** "9 of 14 alerts on" for a tile; "All alerts off" when none. */
export function notificationSummary(on: number, total: number): string {
  if (total <= 0) return 'No alerts for your role';
  if (on === 0) return 'All alerts off';
  return `${on} of ${total} alerts on`;
}

/** "2 phones · 1 app" for the connected-apps tile. */
export function connectedAppsSummary(counts: { devices: number; apps: number }): string {
  const parts: string[] = [];
  if (counts.devices) parts.push(counts.devices === 1 ? '1 phone or tablet' : `${counts.devices} phones and tablets`);
  if (counts.apps) parts.push(counts.apps === 1 ? '1 connected app' : `${counts.apps} connected apps`);
  return parts.length ? parts.join(' · ') : 'Nothing connected yet';
}
